/**
 * EVO — buyruq paneli (Ctrl+K).
 *
 * Asos: TZ v8.0 §8 (Smart Search / Command), TZ v4.0 §7 (Command Center).
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ MUAMMO                                                                    ║
 * ║                                                                          ║
 * ║ Har ekranda O'Z qidiruvi bor, global qidiruv yo'q. `SO-2026-00042` ni    ║
 * ║ bilgan odam avval «bu qaysi ekranda?» degan savolga javob berishi, keyin ║
 * ║ o'sha ekranga o'tishi, keyin u yerda qidirishi kerak. Kuniga o'nlab      ║
 * ║ marta.                                                                   ║
 * ║                                                                          ║
 * ║ Panel bu zanjirni bitta harakatga tushiradi: Ctrl+K → raqam → Enter.     ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ «/» TUGMASIGA TEGILMAYDI                                                 │
 * │                                                                          │
 * │ `search-box.tsx` da «/» ALLAQACHON band: u EKRAN ICHIDAGI qidiruvga     │
 * │ fokusni qaytaradi va xodim buni allaqachon o'rgangan. Ikkinchi ma'no     │
 * │ berish (masalan «/» panelni ochsin) o'sha odatni buzardi.                │
 * │                                                                          │
 * │ Shuning uchun panelning yagona tugmasi — Ctrl+K (macOS da ⌘K).           │
 * │ Bu tugma sanoat standarti (VS Code, Linear, Slack, GitHub) va uni        │
 * │ o'rganish kerak emas.                                                    │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { filterMenu, MENU } from '../config/menu';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n-context';
import { isApiError } from '../lib/api';
import { useAuth, useCan } from '../lib/auth';
import { buildNavCommands, matchNavCommands, type NavCommand } from '../lib/nav-commands';
import {
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_MS,
  SEARCH_TYPE_LABELS,
  globalSearch,
  openHit,
  pushRecent,
  readRecents,
  type SearchGroup,
  type SearchHit,
} from '../lib/search';
import { Input } from './ui/input';
import { Spinner } from './ui/spinner';

export interface CommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/** Yuklanish va bo'sh natija — IKKI XIL holat, ikki xil ko'rinish. */
type Phase = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Ro'yxatdagi bitta qator: yo BO'LIM, yo HUJJAT.
 *
 * Ikkisi bitta massivda turadi va sabab klaviaturada: ↑/↓ ikki ro'yxat
 * o'rtasidagi chegarani sezmasligi kerak. Ularni ajratsak, «pastga bosaverib
 * hujjatlarga o'tish» ishlamasdi va odam sichqonchaga qaytardi.
 */
type PaletteRow =
  | { readonly kind: 'nav'; readonly command: NavCommand }
  | { readonly kind: 'hit'; readonly hit: SearchHit };

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const can = useCan();
  /*
   * `userId` — «oxirgi ochilganlar» kaliti uchun.
   *
   * Ro'yxat FOYDALANUVCHIGA bog'langan (`search.ts` dagi ramka): bitta
   * kompyuterda navbatma-navbat ishlaydigan ikki xodim bir-birining
   * hujjat raqamlarini ko'rmasligi kerak.
   */
  const userId = useAuth().user?.id ?? null;
  const listId = useId();

  /*
   * Bo'lim buyruqlari — RUXSAT o'zgarmaguncha qayta hisoblanmaydi.
   *
   * `t` ham bog'liqlikda: til almashganda nomlar qayta tarjima qilinishi
   * kerak, aks holda RU ga o'tgan odam UZ nomlari bo'yicha qidirardi.
   */
  const navCommands = useMemo(() => buildNavCommands(filterMenu(MENU, can), t), [can, t]);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  /** Panel ochilishidan OLDIN fokusda turgan element — yopilganda unga qaytariladi. */
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [groups, setGroups] = useState<readonly SearchGroup[]>([]);
  const [recents, setRecents] = useState<readonly SearchHit[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // -------------------------------------------------------------------------
  // Ochish/yopish
  // -------------------------------------------------------------------------

  /*
   * Ctrl+K / ⌘K — GLOBAL tinglovchi.
   *
   * `metaKey` macOS uchun: u yerda Ctrl emas, ⌘ ishlatiladi va Ctrl+K
   * terminalda «qatorni o'chirish» ma'nosini saqlaydi. Ikkalasini ham qabul
   * qilamiz — foydalanuvchi o'z odatini olib keladi.
   *
   * `preventDefault` SHART: Firefox'da Ctrl+K manzil qatoridagi qidiruvga
   * o'tadi va panel ochilgani bilan fokus brauzerga ketardi.
   *
   * `search-box.tsx` dagi `isTypingTarget()` ga O'XSHASH tekshiruv bu yerda
   * ATAYLAB YO'Q: «/» ni maydon ichida ushlamaslik kerak (SKU larda `/`
   * uchraydi), Ctrl+K esa HAR JOYDA ishlashi kerak — jadval katagini
   * to'ldirayotgan omborchi ham panelni ocha olishi shart, va `Ctrl` bilan
   * birga bosilgan `k` hech qanday matnga tushmaydi.
   */
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== 'k' && event.key !== 'K') return;
      if (!event.ctrlKey && !event.metaKey) return;
      if (event.altKey) return;

      event.preventDefault();
      // Ochiq panelda Ctrl+K — YOPADI. Bir xil tugma ikki tomonga ishlaydi:
      // shunda «adashib bosdim» holati qo'shimcha harakatsiz orqaga qaytadi.
      onOpenChange(!open);
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  /*
   * Ochilganda: holat NOLDAN boshlanadi va fokus maydonga o'tadi.
   *
   * Eski so'rovni saqlab qolish jozibali ko'rinadi, lekin amalda zarar: odam
   * panelni YANGI savol bilan ochadi va birinchi ishi eski matnni tanlab
   * o'chirish bo'lardi — har safar.
   */
  useEffect(() => {
    if (!open) return;

    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    setQuery('');
    setGroups([]);
    setPhase('idle');
    setActiveIndex(0);
    setRecents(userId === null ? [] : readRecents(userId));

    // `requestAnimationFrame` — element DOM ga qo'yilgandan KEYIN fokus.
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open, userId]);

  const close = useCallback(() => {
    onOpenChange(false);
    /*
     * Fokus qaytariladi — bu «chiroyli tafsilot» emas, KLAVIATURA bilan
     * ishlaydigan odam uchun asosiy shart: aks holda Escape bosgandan keyin
     * fokus `<body>` ga tushadi va u Tab bilan sahifa boshidan qaytadan
     * yurishga majbur bo'ladi.
     */
    restoreFocusRef.current?.focus();
  }, [onOpenChange]);

  // -------------------------------------------------------------------------
  // Qidiruv — debounce bilan
  // -------------------------------------------------------------------------

  const term = query.trim();

  useEffect(() => {
    if (!open) return undefined;

    if (term.length < MIN_SEARCH_LENGTH) {
      setGroups([]);
      setPhase('idle');
      return undefined;
    }

    /*
     * `AbortController` — TARTIB muammosini yopadi.
     *
     * Debounce so'rovlar SONINI kamaytiradi, lekin ularni tartiblamaydi:
     * `lyu` javobi `lyustra` javobidan KEYIN kelishi mumkin va ekranda eski
     * natija qolib ketardi. Eskisini bekor qilish bu holatni umuman mumkin
     * bo'lmagan qiladi.
     */
    const controller = new AbortController();
    setPhase('loading');

    const timer = window.setTimeout(() => {
      void globalSearch(term, controller.signal)
        .then((response) => {
          setGroups(response.groups);
          setPhase('ready');
          // Yangi natijada tanlov BIRINCHI qatorga qaytadi: aks holda 3-qator
          // «osilib» qolib, Enter mutlaqo boshqa hujjatni ochardi.
          setActiveIndex(0);
        })
        .catch((error: unknown) => {
          // Bekor qilingan so'rov — xato EMAS, u ataylab to'xtatilgan.
          if (controller.signal.aborted) return;
          // 400 (juda qisqa so'rov) ham xato ekrani emas: foydalanuvchi
          // hali terayapti va unga qizil xabar ko'rsatish chalg'itadi.
          if (isApiError(error) && error.status === 400) {
            setGroups([]);
            setPhase('ready');
            return;
          }
          setPhase('error');
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, term]);

  // -------------------------------------------------------------------------
  // Ko'rsatiladigan ro'yxat
  // -------------------------------------------------------------------------

  /**
   * Bo'sh so'rovda — «oxirgi ochilganlar».
   *
   * Bu panelning ENG KO'P ishlatiladigan holati: odam kuni bo'yi 3-4 ta hujjat
   * orasida yuradi. Uni qidiruvsiz berish Ctrl+K → ↓ → Enter zanjirini
   * beradi — uch bosish, matnsiz.
   */
  const shownGroups = useMemo<readonly SearchGroup[]>(() => {
    if (term.length >= MIN_SEARCH_LENGTH) return groups;
    if (recents.length === 0) return [];
    /*
     * Oxirgi ochilganlar ATAYLAB bitta ro'yxat: ular VAQT bo'yicha
     * tartiblangan va turlarga bo'lish o'sha tartibni buzardi — «oxirgi»
     * degan so'zning ma'nosi yo'qolardi.
     *
     * `type` bu yerda ishlatilmaydi (sarlavha `isRecents` bo'yicha
     * tanlanadi), lekin `SearchGroup` shakli uni talab qiladi — birinchi
     * yozuvniki olinadi, ro'yxat bo'sh emasligi yuqorida tekshirilgan.
     */
    return [{ type: recents[0]?.type ?? 'product', items: recents }];
  }, [term, groups, recents]);

  const isRecents = term.length < MIN_SEARCH_LENGTH && recents.length > 0;

  /**
   * Mos bo'limlar — SERVERSIZ, har harfda darhol.
   *
   * Chegara hujjat qidiruvi bilan bir xil (`MIN_SEARCH_LENGTH`) va bu
   * ataylab: bitta harfdan keyin ro'yxat 90 banddan o'nlab tasini
   * ko'rsatardi va u yordam emas, shovqin bo'lardi.
   */
  const navMatches = useMemo(
    () => (term.length < MIN_SEARCH_LENGTH ? [] : matchNavCommands(navCommands, term)),
    [navCommands, term],
  );

  /**
   * Klaviatura uchun tekis ro'yxat — guruhlar chegarasidan «o'tib» yuradi.
   *
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ BO'LIMLAR BIRINCHI — VA BU TEZLIK HAQIDA                             │
   * │                                                                      │
   * │ Bo'limlar darhol tayyor, hujjatlar esa 300 ms debounce + tarmoq      │
   * │ so'rovidan keyin keladi. Ya'ni odam yozib bo'lgan zahoti ro'yxatning │
   * │ TEPASI allaqachon to'lgan bo'ladi va Enter darrov ishlaydi.          │
   * │                                                                      │
   * │ Agar hujjatlar tepada tursa, ro'yxat javob kelgan payt sakrab        │
   * │ ketardi: odam «Savdo» ni tanlagan bo'lardi, Enter bosgunicha uning   │
   * │ o'rniga boshqa hujjat kelib qolardi. Bu — eng yomon xato turi,       │
   * │ chunki u faqat sekin tarmoqda ko'rinadi.                             │
   * └──────────────────────────────────────────────────────────────────────┘
   */
  const rows = useMemo<readonly PaletteRow[]>(
    () => [
      ...navMatches.map((command) => ({ kind: 'nav' as const, command })),
      ...shownGroups.flatMap((group) => group.items.map((hit) => ({ kind: 'hit' as const, hit }))),
    ],
    [navMatches, shownGroups],
  );

  // Ro'yxat qisqarganda tanlov chegaradan chiqib qolmasligi kerak.
  useEffect(() => {
    setActiveIndex((index) => (index >= rows.length ? 0 : index));
  }, [rows.length]);

  const choose = useCallback(
    (row: PaletteRow): void => {
      if (row.kind === 'nav') {
        /*
         * Bo'lim «oxirgi ochilganlar» ga QO'SHILMAYDI.
         *
         * U ro'yxat hujjatlar uchun: «kecha ko'rgan fakturam qayerda edi?»
         * Bo'lim esa har doim bir joyda turadi va uni eslab qo'yish
         * ro'yxatni to'ldirib, haqiqiy hujjatlarni siqib chiqarardi.
         */
        close();
        void navigate({ to: row.command.route });
        return;
      }

      if (userId !== null) pushRecent(userId, row.hit);
      close();
      openHit(navigate, row.hit);
    },
    [close, navigate, userId],
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      // Aylanma: oxirgi qatordan pastga bosish birinchisiga qaytaradi.
      // Uzun ro'yxatda bu «oxiriga yetdim» ni his qildiradi va yo'qotmaydi.
      setActiveIndex((index) => (rows.length === 0 ? 0 : (index + 1) % rows.length));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (rows.length === 0 ? 0 : (index <= 0 ? rows.length - 1 : index - 1)));
      return;
    }

    if (event.key === 'Enter') {
      const row = rows[activeIndex];
      if (row) {
        event.preventDefault();
        choose(row);
      }
    }
  };

  /*
   * Tanlangan qator KO'RINISHDA qoladi.
   *
   * `block: 'nearest'` — sahifani sakratmaydi, faqat kerak bo'lganda
   * suradi. Usiz klaviatura bilan 20-qatorga tushgan odam ekranda hech narsa
   * o'zgarmaganini ko'radi va tanlov qayerdaligini bilmaydi.
   */
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  if (!open) return null;

  const showEmpty = phase === 'ready' && rows.length === 0;
  const showIdleEmpty = phase === 'idle' && rows.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[10vh]"
      // Fon bosilganda yopiladi — sichqoncha bilan ishlaydiganlar uchun
      // «orqaga» harakati Escape bilan bir xil natija berishi kerak.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('search.commandTitle')}
        className="flex max-h-[70vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-2xl"
        onKeyDown={handleKeyDown}
      >
        <div className="border-b border-line p-2">
          <Input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label={t('search.commandTitle')}
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={rows[activeIndex] ? rowId(listId, rows[activeIndex]) : undefined}
            aria-autocomplete="list"
            autoComplete="off"
            placeholder={t('search.commandPlaceholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <div ref={listRef} id={listId} role="listbox" aria-label={t('search.commandTitle')} className="min-h-0 flex-1 overflow-y-auto py-1">
          {/*
            YUKLANISH va BO'SH NATIJA — ikki xil ko'rinish.

            Ularni birlashtirish («hech narsa topilmadi» ni yuklanish paytida
            ham ko'rsatish) xodimni noto'g'ri xulosaga olib keladi: u qidiruvni
            tugagan deb o'ylab, mavjud hujjatni qaytadan yaratadi.
          */}
          {phase === 'loading' && (
            <p className="flex items-center gap-2 px-3 py-2 text-sm text-ink-muted">
              <Spinner />
              {t('search.searching')}
            </p>
          )}

          {phase === 'error' && (
            <p className="px-3 py-2 text-sm text-danger">{t('search.commandFailed')}</p>
          )}

          {showEmpty && <p className="px-3 py-2 text-sm text-ink-muted">{t('search.noResults')}</p>}

          {showIdleEmpty && (
            <p className="px-3 py-2 text-sm text-ink-muted">{t('search.commandHint')}</p>
          )}

          {/*
            BO'LIMLAR — alohida sarlavha ostida.
            Hujjat va bo'lim bir xil ko'rinishda tursa, odam «SO-42» va
            «Sotuv buyurtmalari» ni bir qarashda ajrata olmasdi.
          */}
          {navMatches.length > 0 && (
            <section>
              <h2 className="px-3 pt-2 pb-1 text-xs font-semibold tracking-wide text-ink-muted uppercase">
                {t('search.sectionsTitle')}
              </h2>
              <ul>
                {navMatches.map((command, navIndex) => {
                  const active = navIndex === activeIndex;
                  const row: PaletteRow = { kind: 'nav', command };

                  return (
                    <li key={command.id} id={rowId(listId, row)} role="option" aria-selected={active}>
                      <button
                        type="button"
                        data-active={active}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          choose(row);
                        }}
                        onMouseEnter={() => setActiveIndex(navIndex)}
                        className={cn(
                          'flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm',
                          active ? 'bg-brand-soft text-ink' : 'text-ink hover:bg-surface-2',
                        )}
                      >
                        {/*
                          Guruh nomi CHAPDA, hujjat kodi turgan ustunda.
                          Ikki xil ro'yxat bir xil panjarada tursa, ko'z
                          pastga tushganda qayta moslashmaydi.
                        */}
                        <span className="w-36 shrink-0 truncate text-xs text-ink-muted">
                          {command.groupLabel}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{command.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {rows.length > navMatches.length &&
            shownGroups.map((group, groupIndex) => (
              <section key={`${group.type}-${groupIndex}`}>
                {/* Guruh sarlavhasi — `role="presentation"` EMAS: u ekran
                    o'quvchisiga natija turini aytadi va u kontekstning yarmi. */}
                <h2 className="px-3 pt-2 pb-1 text-xs font-semibold tracking-wide text-ink-muted uppercase">
                  {isRecents ? t('search.recentTitle') : t(SEARCH_TYPE_LABELS[group.type])}
                </h2>

                <ul>
                  {group.items.map((hit) => {
                    const row: PaletteRow = { kind: 'hit', hit };
                    /*
                     * Indeks BO'LIMLAR sonidan keyin boshlanadi: `rows`
                     * massivida avval bo'limlar, keyin hujjatlar turadi.
                     * `findIndex` obyekt havolasi bo'yicha izlagani uchun
                     * bu yerda yangi `row` bilan solishtirib bo'lmaydi.
                     */
                    const index =
                      navMatches.length +
                      shownGroups.slice(0, groupIndex).reduce((sum, g) => sum + g.items.length, 0) +
                      group.items.indexOf(hit);
                    const active = index === activeIndex;

                    return (
                      <li key={`${hit.type}-${hit.id}`} id={rowId(listId, row)} role="option" aria-selected={active}>
                        <button
                          type="button"
                          data-active={active}
                          // `onMouseDown` — `onClick` emas: maydon `blur` idan
                          // OLDIN ishlaydi va tanlov yo'qolmaydi.
                          onMouseDown={(event) => {
                            event.preventDefault();
                            choose(row);
                          }}
                          onMouseEnter={() => setActiveIndex(index)}
                          className={cn(
                            'flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm',
                            active ? 'bg-brand-soft text-ink' : 'text-ink hover:bg-surface-2',
                          )}
                        >
                          <span className="evo-num w-36 shrink-0 truncate text-xs text-ink-muted">{hit.code}</span>
                          <span className="min-w-0 flex-1 truncate">
                            {hit.title}
                            {hit.inactive && (
                              <span className="ml-2 text-xs text-ink-muted">({t('search.inactive')})</span>
                            )}
                          </span>
                          {hit.meta !== null && (
                            <span className="evo-num shrink-0 text-xs text-ink-muted">{hit.meta}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
        </div>

        {/* Tugmalar eslatmasi — o'rganiladigan qism. Bir marta ko'rgan odam
            keyin sichqonchaga umuman qaytmaydi (GAP_ANALYSIS §C3). */}
        <p className="border-t border-line px-3 py-1.5 text-xs text-ink-muted">{t('search.commandKeys')}</p>
      </div>
    </div>
  );
}

/**
 * `aria-activedescendant` uchun barqaror id.
 *
 * Bo'lim va hujjat oralig'ida to'qnashuv bo'lmasligi uchun bo'lim id'siga
 * `nav-` old qo'shimchasi qo'yiladi: `product-7` va `nav-...` hech qachon
 * bir xil bo'lmaydi.
 */
function rowId(listId: string, row: PaletteRow): string {
  return row.kind === 'nav'
    ? `${listId}-nav-${row.command.id}`
    : `${listId}-${row.hit.type}-${row.hit.id}`;
}
